import { FRONTEND_ERROR_MESSAGES, FrontendErrorCode } from "@/internals/constants/errorCodes";
import axios from "axios";

interface ApiErrorResponse {
  message?: string;
  code?: string;
  statusCode?: number;
  occurredOn?: string;
  [key: string]: any;
}

// Toast message builder
export interface ErrorMessageOptions {
  showDetails?: boolean;
  includeTimestamp?: boolean;
  includeCode?: boolean;
  fallbackMessage?: string;
}

/**
 * Generic function to handle Axios errors.
 */
export const handleAxiosError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const { response, config } = error;

    if (response) {
      const { status, data } = response;
      const { message = "An error occurred while processing your request." }: ApiErrorResponse =
        data;

      // TODO: Standardize the error response structure from the backend to ensure consistency across all errors.
      console.error(`[API Error] Status: ${status}, Data:`, data);
      console.error(`Request URL: ${config.url}, Method: ${config.method}`);

      switch (status) {
        case 400:
          throw new Error(message || "Bad request. Please try again.");
        case 401:
          throw new Error("Unauthorized. Please log in again.");
        case 403:
          throw new Error("Forbidden. You don't have permission.");
        case 404:
          throw new Error("Resource not found.");
        case 500:
          throw new Error("Server error. Please try again later.");
        default:
          throw new Error(message || "An unknown API error occurred.");
      }
    } else {
      // Handle network errors, timeout, etc.
      console.error("[Unexpected Error]", error);
      throw new Error("An unknown error occurred.");
    }
  } else {
    // Non-Axios errors (e.g., logic errors)
    console.error("[Non-Axios Error]", error);
    throw new Error("An unknown error occurred.");
  }
};

export class ApiErrorMessageBuilder {
  static buildToastMessage(
    error: any,
    options: ErrorMessageOptions = {
      showDetails: false,
      includeTimestamp: true,
      includeCode: false,
      fallbackMessage: "Something went wrong",
    }
  ): string {
    if (axios.isAxiosError(error)) {
      return this.handleAxiosErrorForToast(error, options);
    }

    if (error && typeof error === "object") {
      return this.handleApiErrorResponseForToast(error, options);
    }

    return options.fallbackMessage;
  }

  // Build toast message for frontend errors (non-API errors)
  // ApiErrorMessageBuilder.buildFrontendErrorMessage('download_error', originalError)
  static buildFrontendErrorMessage(
    errorCode: FrontendErrorCode,
    originalError?: any,
    customMessage?: string
  ): string {
    const baseMessage = customMessage || FRONTEND_ERROR_MESSAGES[errorCode] || "An error occurred";
    const timestamp = new Date().toLocaleTimeString();

    let errorDetails = "";
    if (originalError) {
      if (originalError instanceof Error) {
        errorDetails = originalError.message;
      } else if (typeof originalError === "string") {
        errorDetails = originalError;
      }
    }

    let message = `Error [${errorCode}]: ${baseMessage}`;

    if (errorDetails) {
      message += ` ${errorDetails}`;
    }

    message += ` (Time: ${timestamp})`;

    return message;
  }

  private static handleAxiosErrorForToast(error: any, options: ErrorMessageOptions): string {
    const { response } = error;
    const fallbackMessage = options.fallbackMessage;

    if (response && response.data) {
      return this.handleApiErrorResponseForToast(response.data, options);
    }

    if (error.code === "NETWORK_ERROR") {
      return "Network error. Please check your connection.";
    }
    if (error.code === "ECONNABORTED") {
      return "Request timeout. Please try again.";
    }

    return fallbackMessage;
  }

  private static handleApiErrorResponseForToast(
    errorData: ApiErrorResponse,
    options: ErrorMessageOptions
  ): string {
    const { showDetails, includeTimestamp, includeCode, fallbackMessage } = options;

    const message = errorData.message || fallbackMessage;
    const code = errorData.code || "unknown_error";
    const statusCode = errorData.statusCode || 500;
    const occurredOn = errorData.occurredOn;

    const messageParts: string[] = [];

    messageParts.push(this.buildFriendlyErrorMessage(message, statusCode, code, occurredOn));

    if (showDetails) {
      messageParts.push(message);
      if (includeCode && code !== "unknown_error") {
        messageParts.push(`(Code: ${code})`);
      }

      if (includeTimestamp && occurredOn) {
        messageParts.push(`at ${occurredOn}`);
      }
      if (statusCode) {
        messageParts.push(`[Status: ${statusCode}]`);
      }
    }

    return messageParts.join(" | ");
  }

  private static buildFriendlyErrorMessage(
    message: string,
    statusCode?: number,
    code?: string,
    occurredOn?: string
  ): string {
    let friendlyMessage = "Error";

    if (statusCode) {
      friendlyMessage += ` ${statusCode}`;
    }

    if (code) {
      friendlyMessage += ` (${code}): `;
    }

    friendlyMessage += ` ${message}`;

    if (occurredOn) {
      friendlyMessage += ` - at ${occurredOn}`;
    }

    return friendlyMessage;
  }
}
